/**
 * useRecordingLease — the FE half of the native lease/heartbeat (FR-015).
 *
 * Rust has held a lease registry and a `recording_lease_heartbeat` command for
 * a while; nothing on this side has ever called it. The registry is therefore
 * always empty, and `lease_probe_json` reports `isStale: true` for every
 * recording unconditionally — so an orphaned-no-lease trigger built on it would
 * force-terminate every healthy recording at its grace. This hook is what makes
 * that signal truthful. It adds no trigger and changes no behaviour on its own.
 *
 * Identity comes from the native snapshot, never from FE state: the native
 * epoch is the only authority on which `(recordingId, generation)` is live, and
 * inferring it from the UI is exactly the mistake FR-018 exists to prevent. A
 * rejected heartbeat (`false`) means this epoch has been superseded, so the
 * cached identity is dropped and the next tick re-leases from the snapshot.
 *
 * Self-guards with `isTauri()`, the kill switch and authentication, so it is
 * safe to mount at the app root unconditionally and is fully inert with the
 * flag OFF. While no epoch exists it polls the native snapshot on the
 * grace-derived interval ({@link leaseIdleSnapshotPollMs}) — snapshot-only
 * ticks, no heartbeat IPC — then heartbeats at the same cadence once an epoch
 * is live.
 */

import {
  useFeLeaseGraceSecs,
  useIsSelfHealingStopEnabled,
} from '@/app-shell/flags/self-healing-stop-flag';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { recorderFmc } from '@/tauri/controller/recording';
import type { RecordingLeaseParams } from '@/tauri/controller/self-healing-stop';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { useEffect, useRef } from 'react';

/**
 * Heartbeat this many times per grace window. Three gives two consecutive
 * misses of headroom before the watchdog would consider the webview gone —
 * enough to ride out one slow IPC or a brief main-thread stall without
 * shortening the interval to the point of being chatty.
 */
export const HEARTBEATS_PER_GRACE = 3;

/** Never poll faster than this, however short a grace ops configures. */
const MIN_HEARTBEAT_INTERVAL_MS = 5_000;

/**
 * The heartbeat period for a given grace window, in milliseconds. Exported —
 * with {@link HEARTBEATS_PER_GRACE} — for the unit test, because the
 * arithmetic is the whole safety property of this hook: the invariant
 * `leaseHeartbeatIntervalMs(g) * HEARTBEATS_PER_GRACE <= g * 1000` is what
 * keeps a healthy webview from reading stale at every evaluation. It holds
 * only because `MIN_FE_LEASE_GRACE_SECS` clamps the configured grace at the
 * source; the floor below cannot enforce it alone.
 */
export function leaseHeartbeatIntervalMs(graceSecs: number): number {
  return Math.max(
    MIN_HEARTBEAT_INTERVAL_MS,
    Math.floor((graceSecs * 1000) / HEARTBEATS_PER_GRACE)
  );
}

/**
 * Snapshot poll interval while no epoch exists to heartbeat. Same period as
 * {@link leaseHeartbeatIntervalMs} — the distinction is work per tick
 * (`getRecordingStateSnapshot` only, no `recording_lease_heartbeat` IPC), not a
 * slower cadence. Deliberately not a gate: the native snapshot is the only
 * trustworthy signal that a recording is live, and skipping polls while idle
 * would leave a post-reload epoch unleased. Rust mirrors this period and adds
 * one idle-poll headroom before treating a never-leased epoch as orphaned.
 */
export function leaseIdleSnapshotPollMs(graceSecs: number): number {
  return leaseHeartbeatIntervalMs(graceSecs);
}

export function useRecordingLease(): void {
  const enabled = useIsSelfHealingStopEnabled();
  // Parity with the sibling `useSelfHealingStopEvents`: there is no epoch to
  // lease before login, so the login screen must not run this timer at all.
  const { isAuthenticated } = useIsAuthenticated();
  // Read as a hook, not imperatively, so a live Flagsmith change to the grace
  // re-runs the effect below and re-arms the timer on the new period. The
  // sibling `useSelfHealingStopEvents` re-pushes the same payload to Rust on
  // every payload change, so both sides stay aligned when ops lengthens or
  // shortens the grace window.
  const graceSecs = useFeLeaseGraceSecs();
  // The epoch this webview currently holds a lease for. `null` until resolved
  // from the native snapshot, and cleared whenever Rust rejects the heartbeat.
  const leaseRef = useRef<RecordingLeaseParams | null>(null);
  // Set while a tick is in flight, so a slow IPC round-trip cannot stack ticks.
  const inFlightRef = useRef(false);
  // Whether the last tick found an epoch at all — which selects snapshot-only
  // vs heartbeat work on the next tick, NOT whether `leaseRef` is currently
  // populated. A rejected heartbeat nulls `leaseRef` while an epoch very likely
  // still exists (a new recording just claimed one), and dropping to
  // snapshot-only there would leave the successor unleased for a whole period.
  // Only a terminal snapshot proves there is nothing to prove liveness for.
  const hasEpochRef = useRef(true);

  useEffect(() => {
    if (!isTauri() || !enabled || !isAuthenticated) return;

    let cancelled = false;

    const tick = async (): Promise<void> => {
      if (cancelled || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        if (!leaseRef.current) {
          const snapshot = await recorderFmc.getRecordingStateSnapshot();
          if (cancelled) return;
          // A terminal snapshot means there is no epoch to prove liveness for.
          // Heartbeating one anyway would be rejected, and re-leasing on the
          // next tick keeps the recovery path (FR-018) as the single entry.
          if (snapshot.state === 'terminal' || !snapshot.recordingId) {
            hasEpochRef.current = false;
            return;
          }
          leaseRef.current = {
            recordingId: snapshot.recordingId,
            generation: snapshot.generation,
          };
        }
        hasEpochRef.current = true;

        const accepted = await recorderFmc.recordingLeaseHeartbeat(leaseRef.current);
        if (cancelled) return;
        if (!accepted) {
          // Superseded epoch (a new recording claimed one, or this one went
          // terminal). Drop the stale identity so the next tick re-leases from
          // the snapshot rather than heartbeating a recording that is gone.
          //
          // Log the epoch that was refused: a rejection means the FE and the
          // native lifecycle disagree about which recording is live, which is
          // the state you have to reconstruct when the watchdog terminates
          // something and someone asks why. Both fields are opaque native
          // identifiers — no PHI.
          logger.debug('[useRecordingLease] lease rejected; re-leasing from snapshot', {
            recordingId: leaseRef.current.recordingId,
            generation: leaseRef.current.generation,
          });
          leaseRef.current = null;
        }
      } catch (err) {
        // A failed heartbeat must never surface to the clinician: the watchdog
        // grace is the backstop, and the next tick retries.
        logger.warn('[useRecordingLease] lease heartbeat failed', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    // A self-rescheduling `setTimeout` rather than `setInterval`, because the
    // tick *mode* (snapshot-only vs heartbeat) must be re-derived after each
    // tick from `hasEpochRef`. The interval is the same either way; only the
    // IPC work differs. A `setInterval` would latch whichever mode applied when
    // the effect armed, and this effect does not re-run when a recording
    // starts — so an app idle at mount would stay on snapshot-only ticks even
    // after an epoch appears.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const armNext = (): void => {
      if (cancelled) return;
      timer = setTimeout(
        () => {
          void tick().finally(armNext);
        },
        hasEpochRef.current
          ? leaseHeartbeatIntervalMs(graceSecs)
          : leaseIdleSnapshotPollMs(graceSecs)
      );
    };

    // Beat once immediately so a webview reload re-establishes its lease
    // without burning an interval of the grace window. The first period is
    // armed off the back of it, so it reflects whether that beat found an
    // epoch rather than the always-null state before it ran.
    void tick().finally(armNext);

    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      leaseRef.current = null;
      hasEpochRef.current = true;
      // `cancelled` and `timer` are per-effect-run but `inFlightRef` is shared
      // across runs. Left set, a re-arm racing a parked IPC round trip would
      // see it `true`, skip its immediate beat, and wait a whole interval of
      // the (possibly newly-shortened) grace — the exact cost that immediate
      // beat exists to avoid. Safe to clear: the abandoned tick re-checks
      // `cancelled` after every await, and its `finally` only ever writes
      // `false`.
      inFlightRef.current = false;
    };
  }, [enabled, graceSecs, isAuthenticated]);
}
